import { API_ORIGIN } from '@/lib/api-origin';
import { buildOAuthButton, isManualAuthDisabled, useAuthProviders } from '../hooks/useAuthProviders';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { useAuth } from '@/lib/auth/AuthContext';
import { api, isApiError } from '@/lib/api';
import { signInSchema, type SignInValues } from '@/lib/validation/auth.schema';
import { AuthLayout } from '../components/AuthLayout';
import { FormField } from '../components/FormField';
import { OAuthButtons } from '../components/OAuthButtons';
import { GoogleIcon, DiscordIcon } from '../components/BrandIcons';
import { signInTranslations } from './translations';

export function SignIn() {
  const t = useSectionTranslation(signInTranslations);
  const { user, loading, refresh } = useAuth();
  const oauth = useAuthProviders();
  const manualAuthDisabled = isManualAuthDisabled(oauth.providers);
  const showManualForm = !oauth.loading && !manualAuthDisabled;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { rememberMe: true },
  });

  if (!loading && user) {
    const from = searchParams.get('from');
    return <Navigate to={from || '/'} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setSubmitting(true);
    const res = await api.post<{ user: unknown }>('/auth/login', values);
    setSubmitting(false);
    if (isApiError(res)) {
      if (res.error?.code === 'AUTH_INVALID_CREDENTIALS') setServerError(t.errors.invalid);
      else if (res.error?.code === 'NETWORK_ERROR') setServerError(t.errors.network);
      else setServerError(res.error?.message || t.errors.generic);
      return;
    }
    await refresh();
    const from = searchParams.get('from');
    navigate(from || '/');
  });

  return (
    <AuthLayout title={t.page}>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-display-md text-brand-white mb-2">
            {manualAuthDisabled ? t.oauthOnly.heading : t.heading}
          </h1>
          <p className="text-body-sm text-brand-muted">
            {manualAuthDisabled ? t.oauthOnly.subtext : t.subtext}
          </p>
        </div>

        {serverError && (
          <div
            className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger"
            role="alert"
          >
            <AlertCircle size={16} />
            {serverError}
          </div>
        )}

        {oauth.loading && (
          <div className="flex justify-center py-6" aria-busy="true">
            <Loader2 size={20} className="animate-spin text-brand-muted" />
          </div>
        )}

        {showManualForm && (
        <>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormField
            id="email"
            label={t.form.email}
            type="email"
            placeholder={t.form.emailPlaceholder}
            autoComplete="email"
            required
            icon={<Mail size={16} />}
            error={errors.email?.message}
            registration={register('email')}
          />

          <FormField
            id="password"
            label={t.form.password}
            type="password"
            placeholder={t.form.passwordPlaceholder}
            autoComplete="current-password"
            required
            icon={<Lock size={16} />}
            error={errors.password?.message}
            registration={register('password')}
          />

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-brand-muted cursor-pointer">
              <input
                type="checkbox"
                {...register('rememberMe')}
                className="h-4 w-4 rounded border-surface-border bg-surface-card text-accent focus:ring-accent/40"
              />
              {t.form.rememberMe}
            </label>
            <Link to="/auth/forgot-password" className="text-xs text-accent hover:underline">
              {t.form.forgotPassword}
            </Link>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary h-11 w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {t.form.submitting}
              </>
            ) : (
              t.form.submit
            )}
          </button>
        </form>

        <div className="relative py-2">
          <div className="absolute inset-x-0 top-1/2 h-px bg-surface-border" />
          <span className="relative bg-surface-base px-3 text-xs text-brand-muted inline-block start-1/2 -translate-x-1/2">
            {t.divider}
          </span>
        </div>
        </>
        )}

        <OAuthButtons
          providers={[
            buildOAuthButton(oauth.providers, { id: 'google', label: t.oauth.google, href: `${API_ORIGIN}/api/auth/google`, Icon: GoogleIcon }, t.oauth.unavailable),
            buildOAuthButton(oauth.providers, { id: 'discord', label: t.oauth.discord, href: `${API_ORIGIN}/api/auth/discord`, Icon: DiscordIcon, iconColor: 'text-[#5865F2]' }, t.oauth.unavailable),
          ]}
        />

        <p className="text-center text-sm text-brand-muted">
          {t.signup.lead}{' '}
          <Link to="/auth/signup" className="text-accent hover:underline font-medium">
            {t.signup.link}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}